import { latLonToUtm, utmCrs } from './utm.js';

function objectBody(source, reference) {
  const match = String(reference).match(/(\d+)\s+\d+\s+R/);
  if (!match) return String(reference);
  const objectPattern = new RegExp(`(?:^|[\\r\\n])\\s*${match[1]}\\s+\\d+\\s+obj\\b([\\s\\S]*?)\\bendobj\\b`);
  const objectMatch = source.match(objectPattern);
  if (!objectMatch) throw new Error(`GeoPDF object ${match[1]} was not found.`);
  return objectMatch[1];
}

function referencedValue(source, body, key) {
  const reference = body.match(new RegExp(`/${key}\\s+(\\d+\\s+\\d+\\s+R)`));
  return reference ? objectBody(source, reference[1]) : body;
}

function numberArray(body, key) {
  const match = body.match(new RegExp(`/${key}\\s*\\[([^\\]]+)\\]`));
  if (!match) return null;
  const values = match[1].match(/[+-]?(?:\d+\.?\d*|\.\d+)(?:[Ee][+-]?\d+)?/g)?.map(Number) || [];
  return values.every(Number.isFinite) ? values : null;
}

function nearestCorner(points, u, v) {
  return points.reduce((best, point) => {
    const distance = (point.u - u) ** 2 + (point.v - v) ** 2;
    return !best || distance < best.distance ? { ...point, distance } : best;
  }, null);
}

function projectWgs84Pair(latitude, longitude, targetCrs) {
  const projected = latLonToUtm(latitude, longitude, targetCrs.zone, targetCrs.hemisphere);
  return { x: projected.easting, y: projected.northing, latitude, longitude };
}

export function reprojectGeoPdfMetadata(metadata,targetCrs){
  const normalized=utmCrs(targetCrs.zone,targetCrs.hemisphere),controlPoints=metadata.controlPoints.map(point=>({u:point.u,v:point.v,...projectWgs84Pair(point.latitude,point.longitude,normalized)})),cornerTargets=[[0,1],[0,0],[1,0],[1,1]],corners=cornerTargets.map(([u,v])=>nearestCorner(controlPoints,u,v));
  const xs=corners.map(corner=>corner.x),ys=corners.map(corner=>corner.y);
  return {...metadata,targetEpsg:normalized.epsg,targetCrs:normalized.name,projectCrs:normalized,controlPoints,corners:corners.map(({distance,...corner})=>corner),bounds:{minX:Math.min(...xs),minY:Math.min(...ys),minZ:0,maxX:Math.max(...xs),maxY:Math.max(...ys),maxZ:0}};
}

export function extractGeoPdfMetadata(buffer, requestedTargetCrs = null) {
  const source = new TextDecoder('latin1').decode(buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer));
  const viewportReference = source.match(/\/VP\s*\[\s*(\d+\s+\d+\s+R)/)?.[1];
  if (!viewportReference) throw new Error('This PDF does not contain an ISO 32000 georeferenced viewport (/VP).');

  const viewport = objectBody(source, viewportReference);
  const bbox = numberArray(viewport, 'BBox');
  const measure = referencedValue(source, viewport, 'Measure');
  if (!/\/Subtype\s*\/GEO\b/.test(measure)) throw new Error('The PDF viewport does not use a /GEO measurement dictionary.');

  const gpts = numberArray(measure, 'GPTS');
  const lpts = numberArray(measure, 'LPTS') || numberArray(measure, 'Bounds');
  if (!bbox || bbox.length !== 4 || !gpts || !lpts || gpts.length < 8 || gpts.length !== lpts.length) {
    throw new Error('GeoPDF metadata is incomplete (BBox/LPTS/GPTS).');
  }

  const gcs = referencedValue(source, measure, 'GCS');
  const epsg = Number(gcs.match(/\/EPSG\s+(\d+)/)?.[1]);
  if (epsg !== 4326) throw new Error(`GeoPDF CRS EPSG:${epsg || 'unknown'} is not supported yet. This prototype currently auto-detects GeoPDFs with WGS 84 control points.`);

  const latitudes=[],longitudes=[];
  for(let index=0;index+1<gpts.length;index+=2){latitudes.push(gpts[index]);longitudes.push(gpts[index+1]);}
  const centerLatitude=latitudes.reduce((sum,value)=>sum+value,0)/latitudes.length,centerLongitude=longitudes.reduce((sum,value)=>sum+value,0)/longitudes.length;
  const natural=latLonToUtm(centerLatitude,centerLongitude);
  const detectedCrs=utmCrs(natural.zone,natural.hemisphere);
  const targetCrs=requestedTargetCrs?.type==='utm'?utmCrs(requestedTargetCrs.zone,requestedTargetCrs.hemisphere):detectedCrs;

  const controlPoints = [];
  for (let index = 0; index + 1 < lpts.length; index += 2) {
    const projected = projectWgs84Pair(gpts[index], gpts[index + 1],targetCrs);
    controlPoints.push({ u: lpts[index], v: lpts[index + 1], ...projected });
  }
  const cornerTargets = [[0, 1], [0, 0], [1, 0], [1, 1]];
  const corners = cornerTargets.map(([u, v]) => nearestCorner(controlPoints, u, v));
  if (corners.some(corner => !corner || corner.distance > 0.01)) throw new Error('The PDF georeference control points do not cover all four page corners.');

  const xs = corners.map(corner => corner.x), ys = corners.map(corner => corner.y);
  return {
    sourceEpsg: epsg,
    targetEpsg: targetCrs.epsg,
    targetCrs: targetCrs.name,
    detectedCrs,
    projectCrs: targetCrs,
    viewportBBox: bbox,
    controlPoints,
    corners: corners.map(({ distance, ...corner }) => corner),
    bounds: { minX: Math.min(...xs), minY: Math.min(...ys), minZ: 0, maxX: Math.max(...xs), maxY: Math.max(...ys), maxZ: 0 },
  };
}
